import React, { useReducer } from "react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import T from "i18n-react";
import { Tabs } from "antd";
import smoothScroll from "smoothscroll-polyfill";
import "./styles.css";
import MenuItemsPageSkeletonLoading from "../Widgets/Loading/MenuItemsPage";
import MenuContentsContainer from "./MenuContentsContainer";

import SearchBox from "./SearchBox";

const { TabPane } = Tabs;
function searchComponent(index: string | number) {
  return (
    <TabPane
      tab={
        <div className="Flex AlignCenter JustCenter">
          <span className="MenuSearchIcon consumer-search" />
          <span data-component-name="SearchTab" className="tabs">
            {T.translate("Menu.SEARCH")}
          </span>
        </div>
      }
      key={index.toString()}
    />
  );
}

interface ITopMenuState {
  position: number;
  searchIndex: number;
  searchText: string;
  description: string;
  dietaryFilters: any[];
}
const initalState = {
  position: 0,
  searchIndex: 0,
  searchText: "",
  description: "",
  dietaryFilters: [],
};
const TopMenuV2 = () => {
  const menuItemState = useSelector((state: any) => state.MenuItem.tree);
  const menuItemRaw = useSelector((state: any) => state.MenuItem.raw);
  const [currentMainTab, setCurrentMainTab] = useState<number | string>();
  const [mainBar, setMainBar] = useState<JSX.Element[]>([searchComponent(0)]);
  const [subCatBar, setSubCatBar] = useState<JSX.Element[]>([]);
  const [state, dispatch] = useReducer(TopMenuReducer, initalState);
  const { position, description, searchIndex, searchText } = state;

  smoothScroll.polyfill();
  useEffect(() => {
    /**render the main category tabs */
    const tabs = renderTabs();
    setMainBar(tabs);
    // eslint-disable-next-line
  }, [menuItemState]);
  useEffect(() => {
    /**Update the sub categories whenever a main category changes */
    const tabs = renderSubCategories(currentMainTab);
    setSubCatBar(tabs);
    if (parseInt(currentMainTab as string) === searchIndex) {
      onChangeSubTab(searchIndex);
    }
    // eslint-disable-next-line
  }, [currentMainTab, menuItemState]);
  useEffect(() => {
    setActiveTab();
  }, [subCatBar]);
  useEffect(() => {
    if (menuItemState) {
      const categoryKeys = getCategoryHeaders();
      if (categoryKeys[currentMainTab]) {
        const { subCats } = menuItemState[categoryKeys[currentMainTab]];
        for (const key in subCats) {
          if (subCats[key].index === position) {
            dispatch({
              type: "setDescription",
              description: subCats[key].subcatDesc,
            });
          }
        }
      }
    }
    //eslint-disable-next-line
  }, [currentMainTab, position]);
  useEffect(() => {
    if (menuItemState) {
      const key = getCategoryHeaders()[currentMainTab];
      const menuState = menuItemState[key];
      if (key === undefined) {
        /**If its in the search state and not in search index
         * then set the current main tab to the one before*/
        if (mainBar.length > 2 && position !== searchIndex) {
          setCurrentMainTab(1);
        } else if (position !== searchIndex) {
          setCurrentMainTab(0);
        }
      } else if (position > menuState.endIndex) {
        /**If the position is higher than the end index then set to the next tab*/
        if (currentMainTab === 0 && mainBar.length > 2) {
          setCurrentMainTab(1);
        } else {
          setCurrentMainTab(searchIndex);
        }
      } else if (position < menuState.startIndex) {
        /**If the index is less than the start index then go back a page */
        if (currentMainTab === 1 && mainBar.length > 2) {
          setCurrentMainTab(0);
        }
      }
    }
    /**Scroll to the active tab button */
    setActiveTab();
    // eslint-disable-next-line
  }, [position]);
  function setActiveTab() {
    const activeTab = document.querySelectorAll('[aria-selected="true"]')[1];
    if (activeTab) {
      setTimeout(() => {
        /**Check if the active tab still exists,
         * Checks if the scrollInto view is a function
         * and checks for a client height
         */
        if (
          activeTab === null ||
          activeTab === undefined ||
          typeof activeTab.scrollIntoView !== "function" ||
          activeTab.clientHeight === undefined
        ) {
          return;
        }
        activeTab.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }, 0);
    }
  }
  /**renders the main categories */
  const renderTabs = () => {
    const tabs = [];
    let highIndex = 0;
    if (menuItemState !== undefined) {
      const categoryKeys = getCategoryHeaders();
      if (categoryKeys !== undefined) {
        setCurrentMainTab(0);
        for (let i = 0; i < categoryKeys.length; i++) {
          highIndex = menuItemState[categoryKeys[i]].endIndex;
          tabs.push(
            <TabPane
              tab={
                <span
                  data-component-name="CategoryTab"
                  data-category-tab={categoryKeys[i]}
                  className="tabs"
                >
                  {T.translate(`Menu.${categoryKeys[i].toUpperCase()}`)
                    .toString()
                    .toLocaleUpperCase()}
                </span>
              }
              key={i.toString()}
            />
          );
        }
      }
    }
    const searchIndex = highIndex + 1;
    /**Set the search index position */
    dispatch({ type: "setSearchIndex", position: searchIndex });
    tabs.push(searchComponent(searchIndex));
    return tabs;
  };
  /**renders the sub categories headers */
  const renderSubCategories = (mainTab) => {
    if (menuItemState !== undefined) {
      const categoryKeys = getCategoryHeaders();
      const subCatRender = [];
      for (let i = 0; i < categoryKeys.length; i++) {
        /**gets the correct main category */
        if (i === parseInt(mainTab as string)) {
          const subCats = menuItemState[categoryKeys[i]];
          if (subCats !== undefined) {
            const subCatKeys = Object.keys(subCats.subCats);
            if (subCatKeys !== undefined) {
              /**if less than start index or more than index
               * then go the start index */
              if (position < subCats.startIndex) {
                onChangeSubTab(subCats.startIndex);
              } else if (position > subCats.endIndex) {
                onChangeSubTab(subCats.startIndex);
              }
              for (let i = 0; i < subCatKeys.length; i++) {
                const subCat = subCats.subCats[subCatKeys[i]];
                const name = subCat.subcatName.toUpperCase();
                subCatRender.push(
                  <TabPane
                    key={subCat.index}
                    tab={
                      <span
                        data-component-name="SubCategoryTab"
                        data-subcategory-tab={name}
                        className="tabs"
                      >
                        {name}
                      </span>
                    }
                  />
                );
              }
            }
          }
        }
      }
      return subCatRender;
    }
    return [];
  };
  const getCategoryHeaders = () => Object.keys(menuItemState);

  const onChangeMainTab = (activeKey: string | number) => {
    const key = parseInt(activeKey as string);
    setCurrentMainTab(key);
  };
  function textChange(value: string) {
    dispatch({ type: "setSearchText", position: value });
  }
  const onChangeSubTab = (activeKey: string | number) => {
    const keyParsed = parseInt(activeKey as string);
    dispatch({ type: "setPosition", position: keyParsed });
  };
  if (menuItemRaw) {
    return (
      <div className="top_menu_container">
        <div data-component-name="top_menu" className="top_menu">
          <Tabs
            className="topMenuTabs"
            activeKey={`${currentMainTab}`}
            onChange={onChangeMainTab}
            style={{
              borderRadius: "20px 20px 0 0 ",
            }}
          >
            {mainBar}
          </Tabs>

          {position === searchIndex ? (
            <div className="top_menu__filter-wrapper">
              <SearchBox onTextChange={textChange} searchText={searchText} />
            </div>
          ) : (
            <div className="sub_cats">
              <Tabs
                className="subMenu"
                activeKey={`${position}`}
                onChange={onChangeSubTab}
              >
                {subCatBar}
              </Tabs>
            </div>
          )}
        </div>
        {description?.length > 0 && (
          <div className="subCategoryDescription">{description}</div>
        )}
        <MenuContentsContainer
          searchIndex={searchIndex}
          position={position}
          onChangePosition={onChangeSubTab}
          menuItemState={menuItemState}
          menuItemRaw={menuItemRaw}
          searchText={searchText}
        />
      </div>
    );
  } else {
    return <MenuItemsPageSkeletonLoading />;
  }
};

function TopMenuReducer(state: ITopMenuState, action) {
  switch (action.type) {
    case "setPosition":
      return {
        ...state,
        position: parseInt(action.position),
      };
    case "setDescription":
      return {
        ...state,
        description: action.description,
      };
    case "setSearchText":
      console.log("setting text");
      return {
        ...state,
        searchText: action.position.toLowerCase(),
      };

    case "setSearchIndex":
      return { ...state, searchIndex: action.position };
    default:
      return state;
  }
}
export default TopMenuV2;
